import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { User } from '../App';
import { toast } from 'sonner';
import { 
  User as UserIcon, 
  Shield, 
  Bell, 
  Fingerprint, 
  Moon, 
  Download, 
  Cloud, 
  HelpCircle, 
  Phone, 
  LogOut,
  Copy,
  ChevronRight,
  Settings as SettingsIcon,
  Key,
  Calendar,
  Globe,
  Check,
  Plus,
  Sun,
  Database,
  MessageCircle,
  Lock,
  Smartphone,
  Languages,
  Eye,
  EyeOff,
  X,
  Trash2,
  Upload,
  RefreshCw,
  AlertTriangle,
  Info
} from 'lucide-react';
import { i18n, Language } from '../utils/i18n';
import { getBrandName } from '../utils/brand';

interface SettingsProps {
  user: User;
  onLogout: () => void;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  isAdmin: boolean;
  onAdminLogin: (password: string) => boolean;
  eventSettings: {
    startDate: string;
    endDate: string;
  };
  onEventSettingsUpdate: (settings: { startDate: string; endDate: string }) => void;
}

export function Settings({ user, onLogout, darkMode, onToggleDarkMode, isAdmin, onAdminLogin, eventSettings, onEventSettingsUpdate }: SettingsProps) {
  const [notifications, setNotifications] = useState(true);
  const [biometrics, setBiometrics] = useState(true); // 기본값을 true로 변경하여 모든 사용자가 사용 가능하게
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showPinChangeModal, setShowPinChangeModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [localEventSettings, setLocalEventSettings] = useState(eventSettings);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.getCurrentLanguage());

  const handleExportData = () => {
    const userData = {
      nickname: user.nickname,
      walletAddress: user.walletAddress,
      balance: user.balance,
      exportDate: new Date().toISOString()
    };

    const dataStr = JSON.stringify(userData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `vatton-wallet-${user.nickname}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
    toast.success('Data exported successfully!');
  };

  const handleChangePin = () => {
    setShowPinChangeModal(true);
  };

  const handlePinChangeSubmit = async () => {
    if (!newPin || !confirmPin) {
      toast.error('Please enter both PIN fields.');
      return;
    }
    
    if (newPin.length < 4 || newPin.length > 6) {
      toast.error('PIN must be 4-6 digits.');
      return;
    }

    if (newPin !== confirmPin) {
      toast.error('PINs do not match.');
      return;
    }

    try {
      const token = localStorage.getItem('vatton-jwt-token');
      if (!token) {
        console.error('인증 토큰이 없습니다.');
        toast.error(i18n.t('authRequired'));
        return;
      }

      const API_URL = import.meta.env.VITE_API_URL || 'http://159.223.63.184:3601';
      const response = await fetch(`${API_URL}/api/vatton/users/change-pin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          newPin: newPin
        })
      });

      if (!response.ok) {
        throw new Error('PIN 변경 실패');
      }

      const result = await response.json();

      setShowPinChangeModal(false);
      setNewPin('');
      setConfirmPin('');
      toast.success('PIN changed successfully!');
      console.log('PIN 변경 성공:', result);
    } catch (error) {
      console.error('PIN 변경 오류:', error);
      toast.error(i18n.t('pinChangeFailed'));
    }
  };

  const handleLogoutClick = () => {
    if (confirm('Are you sure you want to logout?')) {
      onLogout();
    }
  };

  const handleAdminLoginAttempt = () => {
    if (onAdminLogin(adminPassword)) {
      setShowAdminLogin(false);
      setAdminPassword('');
      toast.success('Admin access granted!');
    } else {
      toast.error('Invalid admin password');
      setAdminPassword('');
    }
  };

  const handleEventSettingsSave = () => {
    onEventSettingsUpdate(localEventSettings);
    toast.success('Event settings updated successfully!');
  };

  const copyWalletAddress = () => {
    if (user?.walletAddress) {
      navigator.clipboard.writeText(user.walletAddress);
      toast.success('Wallet address copied!');
    } else {
      toast.error('Wallet address not available');
    }
  };

  const handleLanguageChange = (language: Language) => {
    setCurrentLanguage(language);
    i18n.setLanguage(language);
    setShowLanguageModal(false);
    toast.success(`Language changed to ${getLanguageName(language)}`);
    window.location.reload();
  };

  const getLanguageName = (lang: Language) => {
    switch (lang) {
      case 'en': return 'English';
      case 'ko': return '한국어';
      case 'id': return 'Bahasa Indonesia';
      default: return 'English';
    }
  };

  const handleBiometricsToggle = async (enabled: boolean) => {
    if (enabled) {
      try {
        if ('credentials' in navigator) {
          const publicKeyCredentialRequestOptions = {
            challenge: new Uint8Array(32),
            timeout: 60000,
            userVerification: 'required' as const
          };
          
          try {
            await navigator.credentials.get({
              publicKey: publicKeyCredentialRequestOptions
            });
            setBiometrics(true);
            toast.success('Biometric authentication enabled!');
          } catch (error) {
            if (confirm('Enable biometric authentication? (This is a demo - actual biometric verification would occur here)')) {
              setBiometrics(true);
              toast.success('Biometric authentication enabled!');
            }
          }
        } else {
          if (confirm('Enable biometric authentication? (This is a demo - actual biometric verification would occur here)')) {
            setBiometrics(true);
            toast.success('Biometric authentication enabled!');
          }
        }
      } catch (error) {
        toast.error('Failed to enable biometric authentication');
      }
    } else {
      setBiometrics(false);
      toast.success('Biometric authentication disabled');
    }
  };

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-light text-foreground">Settings</h1>
            <p className="text-muted-foreground">Manage your account and preferences</p>
          </div>
          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
            <SettingsIcon className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>

        {/* Profile Card */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-500 border-0 text-white">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-2xl font-medium">
                  {user?.nickname?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-medium">{user?.nickname || 'Unknown User'}</h3>
                <p className="text-white/80 text-sm">{getBrandName()} Wallet Member</p>
                <div className="mt-2">
                  <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full">
                    {(user?.balance || 0).toFixed(2)} VTTN
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">Account</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <UserIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Username</p>
                      <p className="text-muted-foreground text-sm">{user?.nickname || 'Unknown User'}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Shield className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Wallet Address</p>
                      <p className="text-muted-foreground text-sm font-mono">
                        {user.walletAddress.slice(0, 10)}...{user.walletAddress.slice(-8)}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copyWalletAddress}
                    className="text-muted-foreground hover:text-foreground p-1 h-auto"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <UserIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Member Since</p>
                      <p className="text-muted-foreground text-sm">
                        {new Date(user.createdAt).toLocaleDateString('en-US', {
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Security Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">Security</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-foreground font-medium">Change PIN</p>
                    <p className="text-muted-foreground text-sm">Update your security PIN</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleChangePin}
                  className="text-muted-foreground hover:text-foreground p-1 h-auto"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Preferences Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">Preferences</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Bell className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Push Notifications</p>
                      <p className="text-muted-foreground text-sm">Receive transaction alerts</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifications}
                    onCheckedChange={setNotifications}
                  />
                </div>
              </div>
              
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Fingerprint className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Biometric Auth</p>
                      <p className="text-muted-foreground text-sm">Use fingerprint or Face ID (Available for all users)</p>
                    </div>
                  </div>
                  <Switch
                    checked={biometrics}
                    onCheckedChange={handleBiometricsToggle}
                  />
                </div>
              </div>
              
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Globe className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Language</p>
                      <p className="text-muted-foreground text-sm">{getLanguageName(currentLanguage)}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowLanguageModal(true)}
                    className="text-muted-foreground hover:text-foreground p-1 h-auto"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Moon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Dark Mode</p>
                      <p className="text-muted-foreground text-sm">Switch between light and dark themes</p>
                    </div>
                  </div>
                  <Switch
                    checked={darkMode}
                    onCheckedChange={onToggleDarkMode}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Management */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">Data</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Download className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Export Data</p>
                      <p className="text-muted-foreground text-sm">Download your wallet data</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled
                    className="text-muted-foreground/50 p-1 h-auto cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Cloud className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Cloud Sync</p>
                      <p className="text-muted-foreground text-sm">Backup to cloud storage</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled
                    className="text-muted-foreground/50 p-1 h-auto cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Server Monitoring Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">{i18n.t('serverMonitoring')}</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <SettingsIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Server Status</p>
                      <p className="text-muted-foreground text-sm">Monitor server performance and health</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled
                    className="text-muted-foreground p-1 h-auto cursor-not-allowed opacity-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>


        {/* Support Section */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground font-medium">Support</h3>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <HelpCircle className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Help & FAQ</p>
                      <p className="text-muted-foreground text-sm">Get answers to common questions</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toast.info('Opening help center.')}
                    className="text-muted-foreground hover:text-foreground p-1 h-auto"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-foreground font-medium">Contact Support</p>
                      <p className="text-muted-foreground text-sm">Get help from our team</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toast.info('Contacting support team.')}
                    className="text-muted-foreground hover:text-foreground p-1 h-auto"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Logout */}
        <Card className="bg-destructive/10 border-destructive/20">
          <CardContent className="p-4">
            <Button
              onClick={handleLogoutClick}
              className="w-full h-12 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-medium rounded-xl flex items-center justify-center gap-3"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </Button>
          </CardContent>
        </Card>

        {/* App Version */}
        <div className="text-center py-4">
          <p className="text-muted-foreground text-sm">{getBrandName()} Wallet v1.0.0</p>
          <p className="text-muted-foreground/60 text-xs mt-1">© 2025 {getBrandName()}. All rights reserved.</p>
        </div>
      </div>

      {/* Admin Login Modal */}
      {showAdminLogin && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Admin Access</h3>
              <p className="text-muted-foreground text-sm">Enter admin password to continue</p>
            </div>

            <div className="space-y-4">
              <div>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Admin Password"
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAdminLoginAttempt();
                    }
                  }}
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => {
                    setShowAdminLogin(false);
                    setAdminPassword('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAdminLoginAttempt}
                  disabled={!adminPassword}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Login
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Select Language</h3>
              <p className="text-muted-foreground text-sm">Choose your preferred language</p>
            </div>

            <div className="space-y-3">
              {(['en', 'ko', 'id'] as Language[]).map((lang) => (
                <button
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border transition-colors ${
                    currentLanguage === lang
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-foreground hover:bg-accent'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">
                      {lang === 'en' && '🇺🇸'}
                      {lang === 'ko' && '🇰🇷'}
                      {lang === 'id' && '🇮🇩'}
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{getLanguageName(lang)}</p>
                      <p className="text-sm opacity-60">
                        {lang === 'en' && 'English'}
                        {lang === 'ko' && '한국어'}
                        {lang === 'id' && 'Bahasa Indonesia'}
                      </p>
                    </div>
                  </div>
                  {currentLanguage === lang && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </button>
              ))}
            </div>

            <Button
              onClick={() => setShowLanguageModal(false)}
              variant="outline"
              className="w-full mt-4 border-border text-muted-foreground hover:bg-accent"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* PIN Change Modal */}
      {showPinChangeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Change PIN</h3>
              <p className="text-muted-foreground text-sm">Enter your new 4-6 digit PIN</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  New PIN
                </label>
                <input
                  type="password"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  placeholder="Enter new PIN"
                  maxLength={6}
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Confirm PIN
                </label>
                <input
                  type="password"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                  placeholder="Confirm new PIN"
                  maxLength={6}
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handlePinChangeSubmit();
                    }
                  }}
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => {
                    setShowPinChangeModal(false);
                    setNewPin('');
                    setConfirmPin('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePinChangeSubmit}
                  disabled={!newPin || !confirmPin}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Update PIN
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}